import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Notification from "../../components/Notification";
import { API_URL } from "../../config";

const UserForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [user, setUser] = useState({
        first_name: "",
        last_name: "",
        role_id: "",
        email: "",
        phone_number: "",
        status: "Active",
        password: "",
        password_confirmation: ""
    });
    const [notification, setNotification] = useState(null);
    const token = localStorage.getItem("access_token");
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [roles, setRoles] = useState([]);
    const statusOptions = ['Active', 'Inactive', 'Suspended', 'UnApproved'];

    const loadUser = useCallback(async () => {
        if (!id) return;

        try {
            const response = await fetch(`${API_URL}/users/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.ok) {
                const data = await response.json();
                setUser({
                    first_name: data.first_name,
                    last_name: data.last_name,
                    role_id: data.role_id,
                    email: data.email,
                    phone_number: data.phone_number,
                    status: data.status,
                    password: "",
                    password_confirmation: ""
                });
            } else if (response.status === 401) {
                navigate("/logout");
            } else {
                const errorData = await response.json();
                setErrors(errorData || {});
                setNotification({ message: errorData.message || "Failed to load user", type: "danger" });
            }
        } catch (error) {
            setNotification({ message: "An error occurred while loading user", type: "danger" });
            console.error("Error:", error);
        }
    }, [id, navigate, token]);

    const loadRoles = useCallback(async () => {
        try {
            const response = await fetch(`${API_URL}/roles?paginate=false`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.ok) {
                const data = await response.json();
                setRoles(data);
            } else {
                throw new Error("Failed to load roles");
            }
        } catch (error) {
            setNotification({ message: error.message || "Failed to load roles", type: "danger" });
            console.error("Error:", error);
        }
    }, [token]);

    useEffect(() => {
        loadRoles();
        if (id) loadUser();
    }, [id, loadUser, loadRoles]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setUser(prev => ({ ...prev, [name]: value }));
        setErrors(prev => ({ ...prev, [name]: "" }));
    };

    const handleSubmit = async (e) => {
        try {
            e.preventDefault();
            setErrors({});
            setLoading(true);

            // Frontend validation for password match
            if (!id && user.password !== user.password_confirmation) {
                setErrors({ password: ["Passwords do not match"] });
                setNotification({ message: "Passwords do not match", type: "danger" });
                return;
            }

            const method = id ? "PUT" : "POST";
            const url = id ? `${API_URL}/users/${id}` : `${API_URL}/users`;

            const formData = new FormData();

            for (const key in user) {
                if (user[key] === null || user[key] === "") {
                    formData.delete(key);
                } else {
                    formData.append(key, user[key]);
                }
            }

            const response = await fetch(url, {
                method,
                headers: {
                    Authorization: `Bearer ${token}`
                },
                body: formData
            });

            if (response.ok) {
                const successMessage = id ? "User updated successfully!" : "User created successfully!";
                setNotification({ type: "success", message: successMessage });
                setTimeout(() => navigate(id ? `/dashboard/users/${id}/details` : "/dashboard/users"), 2000);
            } else if (response.status === 401) {
                navigate("/logout");
            } else {
                const errorData = await response.json();
                setErrors(errorData.errors || {});
                setNotification({ 
                    message: errorData.message || "An error occurred", 
                    type: "danger" 
                });
            }
        } catch (error) {
            setNotification({ message: error.message || "An error occurred", type: "danger" });
            console.error("Error:", error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="container">
            <nav className="level">
                <div className="level-left">
                    <div className="level-item">
                        <p className="title">{id ? "Update User" : "Add User"}</p>
                    </div>
                </div>
                <div className="level-right">
                    <p className="level-item">
                        <Link to="/dashboard/users" className="button primary-bg">
                            <i className="fa fa-arrow-left" aria-hidden="true"></i> &nbsp; Back
                        </Link>
                    </p>
                </div>
            </nav>

            <div className="column dash-section">
                {notification && <Notification type={notification.type} message={notification.message} />}

                <form onSubmit={handleSubmit}>
                    <div className="columns">
                        <div className="column is-half">
                            <div className="field">
                                <label className="label">First Name</label>
                                <div className="control">
                                    <input
                                        type="text"
                                        className={`input ${errors.first_name ? "is-danger" : ""}`}
                                        name="first_name"
                                        value={user.first_name}
                                        onChange={handleChange}
                                        placeholder="First name"
                                        required
                                    />
                                </div>
                                {errors.first_name && <p className="help is-danger">{errors.first_name}</p>}
                            </div>

                            <div className="field">
                                <label className="label">Last Name</label>
                                <div className="control">
                                    <input
                                        type="text"
                                        className={`input ${errors.last_name ? "is-danger" : ""}`}
                                        name="last_name"
                                        value={user.last_name}
                                        onChange={handleChange}
                                        placeholder="Last name"
                                        required
                                    />
                                </div>
                                {errors.last_name && <p className="help is-danger">{errors.last_name}</p>}
                            </div>

                            <div className="field">
                                <label className="label">Role</label>
                                <div className="control">
                                    <div className={`select is-fullwidth ${errors.role_id ? "is-danger" : ""}`}>
                                        <select
                                            name="role_id"
                                            value={user.role_id}
                                            onChange={handleChange}
                                            required
                                        >
                                            <option value="">Select a role</option>
                                            {roles.map(role => (
                                                <option key={role.id} value={role.id}>
                                                    {role.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                                {errors.role_id && <p className="help is-danger">{errors.role_id}</p>}
                            </div>

                            <div className="field">
                                <label className="label">Email</label>
                                <div className="control">
                                    <input
                                        type="email"
                                        className={`input ${errors.email ? "is-danger" : ""}`}
                                        name="email"
                                        value={user.email}
                                        onChange={handleChange}
                                        placeholder="Email"
                                        required
                                    />
                                </div>
                                {errors.email && <p className="help is-danger">{errors.email}</p>}
                            </div>

                            <div className="field">
                                <label className="label">Phone Number</label>
                                <div className="control">
                                    <input
                                        type="text"
                                        className={`input ${errors.phone_number ? "is-danger" : ""}`}
                                        name="phone_number"
                                        value={user.phone_number}
                                        onChange={handleChange}
                                        placeholder="Phone number"
                                        required
                                    />
                                </div>
                                {errors.phone_number && <p className="help is-danger">{errors.phone_number}</p>}
                            </div>

                            <div className="field">
                                <label className="label">Status</label>
                                <div className="control">
                                    <div className={`select is-fullwidth ${errors.status ? "is-danger" : ""}`}>
                                        <select
                                            name="status"
                                            value={user.status}
                                            onChange={handleChange}
                                            required
                                        >
                                            {statusOptions.map(status => (
                                                <option key={status} value={status}>
                                                    {status}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                                {errors.status && <p className="help is-danger">{errors.status}</p>}
                            </div>

                            {!id && (
                                <>
                                    <div className="field">
                                        <label className="label">Password</label>
                                        <div className="control">
                                            <input
                                                type="password"
                                                className={`input ${errors.password ? "is-danger" : ""}`}
                                                name="password"
                                                value={user.password}
                                                onChange={handleChange}
                                                placeholder="Password"
                                                required
                                            />
                                        </div>
                                        {errors.password && <p className="help is-danger">{errors.password}</p>}
                                    </div>

                                    <div className="field">
                                        <label className="label">Confirm Password</label>
                                        <div className="control">
                                            <input
                                                type="password"
                                                className={`input ${errors.password_confirmation ? "is-danger" : ""}`}
                                                name="password_confirmation"
                                                value={user.password_confirmation}
                                                onChange={handleChange}
                                                placeholder="Confirm password"
                                                required
                                            />
                                        </div>
                                        {errors.password_confirmation && (
                                            <p className="help is-danger">{errors.password_confirmation}</p>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    <button 
                        type="submit" 
                        className="button primary-bg is-fullwidth" 
                        disabled={loading}
                    >
                        {loading ? "Processing..." : id ? "Update User" : "Create User"}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default UserForm;